import React, { useEffect, useReducer, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import useTitle from 'utils/customHook/useTitle'
import i18n from 'simple-react-i18n'
import { PATH_PGSSE } from 'home/constants/RouteConstants'
import { getUdiSectorName } from 'pgsse/util/PgsseUtil'
import { Stack } from '@mui/material'
import Breadcrumb from '../common/BreadCrumb'
import PGSSEInfos from '../common/PGSSEInfos'
import ActionTable from './ActionTable'
import { compact, isNil, omit } from 'lodash'
import PgsseAction from 'pgsse/action/PgsseAction'
import useActions from 'utils/customHook/useActions'
import { usedUrl } from 'conf/basepath'
import { REDUCER_ACTIONS } from 'pgsse/constant/PgsseConstants'

const initialState = {
    addedActions: [],
    updatedActions: [],
    deletedActions: [],
}

const reducer = (state, action) => {
    const { type, payload } = action
    switch (type) {
        case REDUCER_ACTIONS.ADD:
            return {
                ...state,
                addedActions: [ ...payload, ...state.addedActions].map((d, indexAdd) => ({ ...d, indexAdd })),
            }
        case REDUCER_ACTIONS.UPDATE:
            if (!isNil(payload.indexAdd)) {
                return {
                    ...state,
                    addedActions: state.addedActions.map(d => d.indexAdd === payload.indexAdd ? payload : d),
                }
            }
            return {
                ...state,
                updatedActions: [
                    ...state.updatedActions.filter(d => d.idAction !== payload.idAction),
                    { ...payload },
                ],
            }
        case REDUCER_ACTIONS.DELETE:
            return {
                ...state,
                addedActions: state.addedActions.filter(d => d.indexAdd !== payload.indexAdd),
                updatedActions: state.updatedActions.filter(d => d.idAction !== payload.idAction),
                deletedActions: compact([ ...state.deletedActions, payload.idAction ]),
            }
        case REDUCER_ACTIONS.RESET:
            return initialState
        default:
            throw new Error()
    }
}

const PgsseActionApp = () => {
    const {
        distributionUnits,
        sectors,
        diagnostic,
        accountUser,
    } = useSelector(store => ({
        distributionUnits: store.DistributionUnitReducer.distributionUnits,
        sectors: store.SectorReducer.sectors,
        diagnostic: store.PgsseReducer.diagnostic,
        accountUser: store.AccountReducer.accountUser,
    }), shallowEqual)

    const dispatch = useDispatch()

    const { idDiagnostic } = useParams()

    const [readMode, setReadMode] = useState(true)
    const [stateAUD, dispatchStateAUD] = useReducer(reducer, initialState)

    useEffect(() => {
        dispatch(PgsseAction.fetchDiagnostic(idDiagnostic))
        dispatch(PgsseAction.fetchDiagnosticDangers(idDiagnostic))
        dispatch(PgsseAction.fetchDiagnosticElements(idDiagnostic))
        dispatch(PgsseAction.fetchDiagnosticActions(idDiagnostic))
    }, [dispatch, idDiagnostic])

    useActions(() => {
        const actions = readMode ? {
            edit: () => setReadMode(false),
            play: () => window.open(`${usedUrl}#/pgsse/diagnostic/${idDiagnostic}`, '_blank').focus(),
        } : {
            cancel: () => {
                dispatchStateAUD({ type: REDUCER_ACTIONS.RESET })
                setReadMode(true)
            },
            save: () => {
                dispatch(PgsseAction.updateActions(idDiagnostic, stateAUD)).then(() => {
                    dispatch(PgsseAction.fetchDiagnosticActions(idDiagnostic))
                    dispatchStateAUD({ type: REDUCER_ACTIONS.RESET })
                    setReadMode(true)
                })
            },
            play: () => window.open(`${usedUrl}#/pgsse/diagnostic/${idDiagnostic}`, '_blank').focus(),
        }
        return accountUser.consultant === '1' ? omit(actions, ['edit', 'save', 'cancel']) : actions
    }, [readMode, diagnostic, idDiagnostic, stateAUD, accountUser])

    useTitle(() => {
        const udi = distributionUnits.find(du => du.id === diagnostic.idUdi)
        const sector = sectors.find(s => s.id === diagnostic.idSector)
        return [{
            title: i18n.pgsse,
            href: PATH_PGSSE,
        }, {
            title: i18n.phase3,
            href: `${PATH_PGSSE}/phase3`,
        }, {
            title: getUdiSectorName(udi, sector),
            href: `${PATH_PGSSE}/phase3/${diagnostic.idDiag}`,
        }]
    }, [diagnostic])

    return (
        <Stack spacing={1} sx={{ padding: '1rem' }}>
            <Breadcrumb />
            <PGSSEInfos readMode diagnostic={diagnostic}/>
            <ActionTable
                readMode={readMode}
                stateAUD={stateAUD}
                dispatchStateAUD={dispatchStateAUD}
            />
        </Stack>
    )
}

export default PgsseActionApp
